<template>
  <div class="w-full grid grid-cols-1 gap-6 lg:grid-cols-2 items-center">
    <div v-if="showCopyBlock" :class="contentOrderClass">
      <slot name="copyBlock">
        <component :is="renderCustomElements(copyBlock)" />
      </slot>
    </div>
    <div v-if="showMediaBlock" :class="imageOrderClass">
      <template v-if="!isVideo">
        <slot name="mediaBlockCopy" />
      </template>
      <template v-else>
        <SingleVideoClient
          :video="mediaIsVideo"
          :is-active="isActive"
          @activate-video="isActive = true"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, type VNode } from 'vue';
import { useWindowSize } from '@vueuse/core';
import SingleVideoClient from '@/components/global/paragraph/video/Default.client.vue';
import type { Video } from '~/types';

const { renderCustomElements } = useDrupalCe();

const { copyBlock } = defineSlots<{
  mediaBlockCopy(): VNode[];
  copyBlock(): VNode[];
}>();

const alternateDirection = inject<'1' | '0'>('alternateDirection', '0');
const childCounter = inject<{ value: number }>('alternateChildCounter', ref(0));
const index = childCounter.value++;

const isContentOnLeft = computed((): boolean => {
  const isEven = index % 2 === 0;
  return alternateDirection === '1' ? !isEven : isEven;
});

const contentOrderClass = computed((): string => {
  return isContentOnLeft.value ? 'order-1 lg:me-6' : 'order-2 lg:ms-6';
});

const imageOrderClass = computed((): string => {
  return isContentOnLeft.value ? 'order-2' : 'order-1';
});

const isActive = ref(false);
const slots = useSlots();
const { width } = useWindowSize();
const isMobile = computed(() => width.value < 1024);
const hasCopyBlock = computed(() => Boolean(slots.copyBlock?.().length));
const hasMediaBlock = computed(() =>
  Boolean(slots.mediaBlockCopy?.().length)
);
const showCopyBlock = computed(() => !isMobile.value || hasCopyBlock.value);
const showMediaBlock = computed(() => !isMobile.value || hasMediaBlock.value);
const mediaIsVideo = computed<Video | undefined>(() => {
  const mediaSlotNodes = slots.mediaBlockCopy?.() ?? [];
  const mediaNodeVideo = mediaSlotNodes[0]?.props as Video | undefined;
  return mediaNodeVideo;
});

const isVideo = computed((): boolean => Boolean(mediaIsVideo.value?.video));
</script>
